import clsx from "clsx";
import { referralCtaButtonClass } from "screens/Dashboard/utils/classes";
import { AnimatePresence, motion } from "framer-motion";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";

type TInput = {
  email: string;
};

function validateEmail(email: string) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

const STORAGE_KEY = "userEmail";

const MAILCHIMP_FORM_ACTION = process.env.REACT_APP_MAILCHIMP_FORM_ACTION;
const MAILCHIMP_USER_ID = process.env.REACT_APP_MAILCHIMP_USER_ID;
const MAILCHIMP_AUDIENCE_ID = process.env.REACT_APP_MAILCHIMP_AUDIENCE_ID;
const MAILCHIMP_FORM_ID = process.env.REACT_APP_MAILCHIMP_FORM_ID;
const MAILCHIMP_HONEYPOT_FIELD = process.env.REACT_APP_MAILCHIMP_HONEYPOT_FIELD;
const MAILCHIMP_SUBSCRIBE_VALUE = "Subscribe";

const SubscribeBlock = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<TInput>();
  const emailValue = watch("email");

  const emailInputRef = useRef<HTMLInputElement | null>(
    null
  ) as MutableRefObject<HTMLInputElement | null>;

  useEffect(() => {
    const input = emailInputRef.current;
    if (input) {
      input.blur();

      const preventInitialFocus = (e: FocusEvent) => {
        input.blur();
      };

      input.addEventListener("focus", preventInitialFocus);

      const timer = setTimeout(() => {
        input.removeEventListener("focus", preventInitialFocus);
      }, 100);

      return () => {
        clearTimeout(timer);
        input.removeEventListener("focus", preventInitialFocus);
      };
    }
  }, []);

  const onSubmit: SubmitHandler<TInput> = async (data) => {
    if (!validateEmail(data.email)) {
      setError("email", {
        type: "manual",
        message:
          t("subscribeCard.errors.invalidEmail") || "Invalid email format.",
      });
      setTimeout(() => clearErrors("email"), 3000);
      return;
    }

    const storageEmail = localStorage.getItem(STORAGE_KEY);
    if (storageEmail === data.email) {
      setError("email", {
        type: "manual",
        message:
          t("subscribeCard.errors.alreadySubscribed") ||
          "You are already subscribed.",
      });
      setTimeout(() => clearErrors("email"), 3000);
      return;
    }

    if (
      !MAILCHIMP_FORM_ACTION ||
      !MAILCHIMP_USER_ID ||
      !MAILCHIMP_AUDIENCE_ID ||
      !MAILCHIMP_FORM_ID
    ) {
      setError("email", {
        type: "manual",
        message:
          t("subscribeCard.errors.missingKeys") ||
          "Subscription keys are missing.",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("EMAIL", data.email);
      formData.append("u", MAILCHIMP_USER_ID);
      formData.append("id", MAILCHIMP_AUDIENCE_ID);
      formData.append("f_id", MAILCHIMP_FORM_ID);
      formData.append("subscribe", MAILCHIMP_SUBSCRIBE_VALUE);
      if (MAILCHIMP_HONEYPOT_FIELD) {
        formData.append(MAILCHIMP_HONEYPOT_FIELD, "");
      }

      await fetch(MAILCHIMP_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });

      localStorage.setItem(STORAGE_KEY, data.email);
      reset();
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      setError("email", {
        type: "manual",
        message:
          t("subscribeCard.errors.somethingWentWrong") ||
          "Something went wrong.",
      });
    }
  };

  const { ref: registerRef, ...emailField } = register("email");

  return (
    <article className="p-[12px] max-lg:p-[12px] w-full bg-[#161616] rounded-[16px] max-lg:rounded-[12px] border border-white/[0.06]">
      <div className="flex flex-col gap-[8px] max-lg:gap-[8px] mb-[9px] max-lg:mb-[12px]">
        <h3 className="text-[16px] max-lg:text-[16px] font-luxora-regular leading-[1.1] text-white/90">
          Subscribe to follow updates
        </h3>
      </div>

      <div className="relative h-[22px] max-lg:h-[19px] ">
        <AnimatePresence mode="wait">
          {(errors.email?.message || isSuccess) && (
            <motion.p
              key={errors.email?.message ? "error" : "success"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className={clsx(
                "absolute text-[12px] max-lg:text-[12px] font-luxora-regular",
                errors.email?.message ? "text-red-400" : "text-emerald-400"
              )}
            >
              {errors.email?.message ||
                t("subscribeCard.successMessage") ||
                "Subscribed successfully!"}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        action={MAILCHIMP_FORM_ACTION || undefined}
        method="post"
        className="relative z-20"
      >
        <label htmlFor="modal-email-input" className="sr-only">
          {t("subscribeCard.emailLabel") || "Your Email"}
        </label>
        <input
          {...emailField}
          ref={(el) => {
            registerRef(el);
            emailInputRef.current = el;
          }}
          placeholder={
            t("subscribeCard.emailPlaceholder") || "Enter your email"
          }
          autoFocus={false}
          type="text"
          inputMode="email"
          autoComplete="off"
          id="modal-email-input"
          className="bg-[#252525] placeholder:text-white/35 max-lg:h-[48px] text-white font-luxora-regular max-lg:pl-[16px] text-[16px] max-lg:text-[12px] leading-[1.1] pl-[16px] pr-[7rem] max-lg:pr-[105px] rounded-[16px] max-lg:rounded-[12px] h-[49px]
          w-full outline-none overflow-x-auto overflow-y-hidden whitespace-nowrap border border-white/[0.06]"
          style={{ textOverflow: "clip" }}
        />

        <input type="hidden" name="EMAIL" value={emailValue ?? ""} readOnly />
        {MAILCHIMP_HONEYPOT_FIELD && (
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-5000px" }}
          >
            <input type="text" name={MAILCHIMP_HONEYPOT_FIELD} tabIndex={-1} />
          </div>
        )}

        <div className="absolute right-[4px] max-lg:right-[4px] top-1/2 -translate-y-1/2">
          <button
            disabled={isSubmitting}
            type="submit"
            name="subscribe"
            value={MAILCHIMP_SUBSCRIBE_VALUE}
            className={clsx(
              referralCtaButtonClass,
              "font-luxora-medium !justify-center text-black max-lg:text-16m text-sm leading-[1.1] max-lg:py-[8px] max-lg:px-[15px] !py-[7px] !px-[14px]",
              isSubmitting && "!opacity-75"
            )}
          >
            {t("subscribeCard.subscribeButton") || "Subscribe"}
          </button>
        </div>
      </form>
    </article>
  );
};

export default SubscribeBlock;
